import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";

// Live seed snapshot from /app/backend/market_seed.py (H1 2026 + top areas).
// Hard-coded here so the comparison shows both sides at a glance without an
// extra API round-trip. Keep in sync manually if the seed values change.
const LIVE = {
  h1_total_tx: 112850,
  h1_total_aed_bn: 419.94,
  h1_sales_tx: 86005,
  h1_sales_aed_bn: 286.44,
  offplan_pct: 68,
  ready_pct: 32,
  quarters: [
    { label: "Q1 2026", transactions: 56789, aed_bn: 210.32 },
    { label: "Q2 2026", transactions: 54684, aed_bn: 210.72 },
  ],
  // Marketing-name → seed transactions_last_quarter + AED/sqft
  areas: {
    "Dubai Marina": { tx_q: 2650, aed_sqft: 2058, yield: 6.9 },
    "Downtown Dubai": { tx_q: 2140, aed_sqft: 3011, yield: 5.8 },
    "JVC": { tx_q: 3480, aed_sqft: 1510, yield: 8.1 },
    "Business Bay": { tx_q: 2860, aed_sqft: 2020, yield: 6.8 },
    "Palm Jumeirah": { tx_q: 620, aed_sqft: 5100, yield: 5.1 },
    "Dubai Hills Estate": { tx_q: 1820, aed_sqft: 1850, yield: 6.2 },
    "Dubai Creek Harbour": { tx_q: 2210, aed_sqft: 2340, yield: 6.4 },
    "Arjan": { tx_q: 1690, aed_sqft: 1250, yield: 8.4 },
    "Dubai South": { tx_q: 1420, aed_sqft: 1080, yield: 7.6 },
    "MBR City": { tx_q: 1830, aed_sqft: 2210, yield: 6.5 },
    "Town Square": { tx_q: 1240, aed_sqft: 1150, yield: 7.4 },
    "Meydan (Nad Al Sheba)": { tx_q: 1180, aed_sqft: 2050, yield: 6.3 },
    "JLT": { tx_q: 1560, aed_sqft: 1780, yield: 7.1 },
    "Dubai Silicon Oasis": { tx_q: 890, aed_sqft: 1050, yield: 7.9 },
    "Arabian Ranches": { tx_q: 540, aed_sqft: 1720, yield: 5.6 },
  },
};

const fmtAED = (n) => (n == null ? "—" : `AED ${Math.round(n).toLocaleString()}`);
const fmtBn = (n) => (n == null ? "—" : `AED ${n.toFixed(2)}B`);
const fmtNum = (n) => (n == null ? "—" : n.toLocaleString());
const fmtPct = (n) => (n == null ? "—" : `${n}%`);

function delta(live, fresh) {
  if (live == null || fresh == null || live === 0) return null;
  const d = ((fresh - live) / live) * 100;
  return d;
}

function DeltaBadge({ live, fresh, invert = false }) {
  const d = delta(live, fresh);
  if (d == null) return null;
  const positive = invert ? d < 0 : d > 0;
  const cls = Math.abs(d) < 3 ? "text-propertic-muted" : positive ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={`text-[11px] font-mono-brand ${cls}`}>
      {d > 0 ? "+" : ""}{d.toFixed(1)}%
    </span>
  );
}

function Row({ label, live, fresh, unit = "", inverted = false }) {
  return (
    <tr className="border-b border-black/5">
      <td className="py-2 pr-3 text-[13px] text-propertic-navy">{label}</td>
      <td className="py-2 pr-3 text-[13px] text-propertic-muted tabular-nums">{live}{unit}</td>
      <td className="py-2 pr-3 text-[13px] text-propertic-navy font-semibold tabular-nums">
        {fresh == null ? <span className="text-propertic-muted italic text-[11px]">pending fetch</span> : `${fresh}${unit}`}
      </td>
      <td className="py-2 text-right">
        {typeof live === "number" && typeof fresh === "number" && (
          <DeltaBadge live={live} fresh={fresh} invert={inverted} />
        )}
      </td>
    </tr>
  );
}

export default function InsightsPreview() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, sum, m, w, a] = await Promise.all([
        api.get("/dld-test/status").then((r) => r.data).catch(() => null),
        api.get("/dld-test/summary").then((r) => r.data).catch(() => null),
        api.get("/dld-test/monthly").then((r) => r.data).catch(() => ({ months: [] })),
        api.get("/dld-test/weekly").then((r) => r.data).catch(() => ({ weeks: [] })),
        api.get("/dld-test/areas/mapped").then((r) => r.data).catch(() => ({ areas: [] })),
      ]);
      setStatus(s);
      setSummary(sum);
      setMonthly(m?.months || []);
      setWeekly(w?.weeks || []);
      setAreas(a?.areas || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000); // auto-refresh every 20s while fetch runs
    return () => clearInterval(id);
  }, []);

  const fetchState = status?.fetch?.state;
  const aggState = status?.aggregate?.state;
  const rowsKept = status?.fetch?.rows_kept || 0;
  const serverTotal = status?.fetch?.server_total || 0;
  const pct = serverTotal ? Math.min(100, (rowsKept / serverTotal) * 100) : 0;

  const dldTotals = {
    tx: summary?.total_transactions,
    aed_bn: summary?.total_aed_bn,
    sales_tx: summary?.sales_count,
    offplan_pct: summary?.offplan_share_pct,
    ready_pct: summary?.ready_share_pct,
  };

  return (
    <div className="min-h-screen bg-propertic-paper text-propertic-navy px-4 sm:px-8 lg:px-16 py-10">
      <Helmet>
        <title>Insights Preview (Internal) · The Propertic</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="max-w-6xl mx-auto mb-8">
        <p className="font-mono-brand text-[11px] tracking-[0.2em] uppercase text-propertic-gold mb-2">
          Internal preview · not linked from nav · noindex
        </p>
        <h1 className="font-editorial text-3xl sm:text-4xl leading-tight mb-2" data-testid="insights-preview-title">
          Live DLD Open Data vs current Propertic seed
        </h1>
        <p className="text-sm text-propertic-muted max-w-3xl">
          Fresh data pulled from{" "}
          <a href="https://gateway.dubailand.gov.ae/open-data/transactions" target="_blank" rel="noreferrer" className="text-propertic-gold underline">
            gateway.dubailand.gov.ae/open-data/transactions
          </a>
          {" "}(Dubai Land Department open-data gateway, same feed as Dubai Pulse), keep window <strong>Jan 1 2026 → today</strong>.
          The right column is what would appear on the live site once we cut over.
        </p>
      </header>

      {/* Fetch progress card */}
      <section className="max-w-6xl mx-auto mb-8 border border-propertic-gold/30 rounded-2xl p-5 bg-white/70" data-testid="fetch-progress">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-muted mb-1">
              Ingest status
            </p>
            <p className="font-editorial text-lg">
              {fetchState === "done" ? "Fetch complete" : fetchState === "running" ? "Fetching from DLD gateway…" : "Idle"}
            </p>
          </div>
          <p className="text-[13px] text-propertic-muted tabular-nums">
            {fmtNum(rowsKept)} / {fmtNum(serverTotal)} rows
          </p>
        </div>
        <div className="h-2 bg-propertic-sand rounded-full overflow-hidden">
          <div
            className="h-full bg-propertic-gold transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-[11px] text-propertic-muted font-mono-brand">
          <span>pages: {status?.fetch?.pages_done ?? 0}</span>
          <span>window: {status?.fetch?.keep_window?.start} → {status?.fetch?.keep_window?.end}</span>
          <span>agg: {aggState || "not run"}</span>
          <span>last: {status?.fetch?.last_page_at?.slice(11, 19) ?? "—"}</span>
        </div>
        {fetchState === "done" && aggState !== "done" && (
          <p className="mt-3 text-[12px] text-amber-700">
            Fetch complete but aggregation not yet run. Trigger via <code>POST /api/dld-test/aggregate</code> with the cron secret.
          </p>
        )}
      </section>

      {/* Dubai-wide comparison */}
      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="font-editorial text-2xl mb-4">1a. H1 2026 (Jan to Jun), like-for-like validation</h2>
        <p className="text-[13px] text-propertic-muted mb-3">
          The live seed's headline H1 numbers vs the exact same window (Jan 1 → Jun 30 2026) computed from the fresh DLD gateway. If these match cleanly, the fresh feed is trustworthy for the full swap.
        </p>
        <div className="overflow-x-auto rounded-xl border border-propertic-gold/40 bg-white/80">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-propertic-sand/70 text-left text-[10px] uppercase tracking-[0.1em] text-propertic-muted">
                <th className="py-2 px-3">Metric</th>
                <th className="py-2 px-3">Live seed (H1)</th>
                <th className="py-2 px-3">Fresh DLD (H1)</th>
                <th className="py-2 px-3 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Total transactions" live={fmtNum(LIVE.h1_total_tx)} fresh={summary?.h1_2026?.total_transactions == null ? null : fmtNum(summary.h1_2026.total_transactions)} />
              <Row label="Total value" live={fmtBn(LIVE.h1_total_aed_bn)} fresh={summary?.h1_2026?.total_aed_bn == null ? null : fmtBn(summary.h1_2026.total_aed_bn)} />
              <Row label="Sales transactions" live={fmtNum(LIVE.h1_sales_tx)} fresh={summary?.h1_2026?.sales_count == null ? null : fmtNum(summary.h1_2026.sales_count)} />
              <Row label="Sales value" live={fmtBn(LIVE.h1_sales_aed_bn)} fresh={summary?.h1_2026?.sales_aed_bn == null ? null : fmtBn(summary.h1_2026.sales_aed_bn)} />
              <Row label="Off-plan share (Sales only)" live={fmtPct(LIVE.offplan_pct)} fresh={summary?.h1_2026?.sales_offplan_share_pct == null ? null : fmtPct(summary.h1_2026.sales_offplan_share_pct)} />
              <Row label="Ready share (Sales only)" live={fmtPct(LIVE.ready_pct)} fresh={summary?.h1_2026?.sales_ready_share_pct == null ? null : fmtPct(summary.h1_2026.sales_ready_share_pct)} />
              <Row label="Median sale price" live="—" fresh={summary?.h1_2026?.median_sale_aed == null ? null : fmtAED(summary.h1_2026.median_sale_aed)} />
              <Row label="Median AED / sqft" live="—" fresh={summary?.h1_2026?.median_aed_per_sqft == null ? null : `AED ${summary.h1_2026.median_aed_per_sqft.toLocaleString()}`} />
            </tbody>
          </table>
        </div>
      </section>

      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="font-editorial text-2xl mb-4">1b. YTD 2026 (Jan 1 → today), what the new live totals would show</h2>
        <div className="overflow-x-auto rounded-xl border border-black/10 bg-white/70">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-propertic-sand/50 text-left text-[10px] uppercase tracking-[0.1em] text-propertic-muted">
                <th className="py-2 px-3">Metric</th>
                <th className="py-2 px-3">Current live seed (H1 2026)</th>
                <th className="py-2 px-3">Fresh DLD gateway (Jan 1 → today)</th>
                <th className="py-2 px-3 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Total transactions" live={fmtNum(LIVE.h1_total_tx)} fresh={dldTotals.tx == null ? null : fmtNum(dldTotals.tx)} />
              <Row label="Total value" live={fmtBn(LIVE.h1_total_aed_bn)} fresh={dldTotals.aed_bn == null ? null : fmtBn(dldTotals.aed_bn)} />
              <Row label="Sales transactions" live={fmtNum(LIVE.h1_sales_tx)} fresh={dldTotals.sales_tx == null ? null : fmtNum(dldTotals.sales_tx)} />
              <Row label="Off-plan share" live={fmtPct(LIVE.offplan_pct)} fresh={dldTotals.offplan_pct == null ? null : fmtPct(dldTotals.offplan_pct)} />
              <Row label="Ready share" live={fmtPct(LIVE.ready_pct)} fresh={dldTotals.ready_pct == null ? null : fmtPct(dldTotals.ready_pct)} />
              <Row label="Median sale price" live="—" fresh={summary?.median_sale_aed == null ? null : fmtAED(summary.median_sale_aed)} />
              <Row label="Median AED / sqft (sales)" live="—" fresh={summary?.median_aed_per_sqft == null ? null : `AED ${summary.median_aed_per_sqft.toLocaleString()}`} />
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-propertic-muted italic">
          Live seed covers only H1 (Jan to Jun). Fresh DLD covers Jan 1 → today, so higher totals are expected, not a bug.
        </p>
      </section>

      {/* Monthly breakdown (replaces quarterly) */}
      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="font-editorial text-2xl mb-4">
          2. Monthly breakdown <span className="text-sm text-propertic-muted">(fresh, would replace the quarterly chart)</span>
        </h2>
        {monthly.length === 0 ? (
          <p className="text-sm text-propertic-muted italic">Aggregation not yet run, waiting for fetch to complete + aggregate.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-black/10 bg-white/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-propertic-sand/50 text-left text-[10px] uppercase tracking-[0.1em] text-propertic-muted">
                  <th className="py-2 px-3">Month</th>
                  <th className="py-2 px-3 tabular-nums">Transactions</th>
                  <th className="py-2 px-3 tabular-nums">Total AED (bn)</th>
                  <th className="py-2 px-3 tabular-nums">Sales</th>
                  <th className="py-2 px-3 tabular-nums">Off-plan %</th>
                  <th className="py-2 px-3 tabular-nums">Median AED/sqft</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.year_month} className="border-t border-black/5">
                    <td className="py-2 px-3 font-semibold">{m.year_month}</td>
                    <td className="py-2 px-3 tabular-nums">{fmtNum(m.tx_count)}</td>
                    <td className="py-2 px-3 tabular-nums">{m.total_aed_bn}</td>
                    <td className="py-2 px-3 tabular-nums">{fmtNum(m.sales_count)}</td>
                    <td className="py-2 px-3 tabular-nums">{m.offplan_share_pct}%</td>
                    <td className="py-2 px-3 tabular-nums">{m.median_aed_per_sqft == null ? "—" : m.median_aed_per_sqft.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-propertic-sand/30 border-t-2 border-propertic-gold/40">
                  <td className="py-2 px-3 text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Live quarters (for reference)</td>
                  <td className="py-2 px-3 tabular-nums text-propertic-muted">
                    {fmtNum(LIVE.quarters[0].transactions)} / {fmtNum(LIVE.quarters[1].transactions)}
                  </td>
                  <td className="py-2 px-3 tabular-nums text-propertic-muted">
                    {LIVE.quarters[0].aed_bn} / {LIVE.quarters[1].aed_bn}
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* Weekly recap (top 8 most recent) */}
      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="font-editorial text-2xl mb-4">
          3. Weekly recap {weekly.length > 0 && <span className="text-sm text-propertic-muted">({weekly.length} weeks available)</span>}
        </h2>
        {weekly.length === 0 ? (
          <p className="text-sm text-propertic-muted italic">Weekly aggregation not yet available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {weekly.slice(-8).reverse().map((w) => (
              <div key={w.iso_week} className="rounded-xl border border-black/10 p-4 bg-white/70">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-editorial text-lg">{w.iso_week}</p>
                  <p className="text-[10px] font-mono-brand tracking-[0.1em] uppercase text-propertic-muted">
                    {w.start_date} → {w.end_date}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[13px]">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Transactions</p>
                    <p className="font-semibold tabular-nums">{fmtNum(w.tx_count)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Total value</p>
                    <p className="font-semibold tabular-nums">{fmtBn(w.total_aed_bn)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Off-plan</p>
                    <p className="tabular-nums">{w.offplan_share_pct}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Median AED/sqft</p>
                    <p className="tabular-nums">{w.median_aed_per_sqft ? w.median_aed_per_sqft.toLocaleString() : "—"}</p>
                  </div>
                </div>
                {w.biggest_sale && (
                  <div className="mt-3 border-t border-black/5 pt-2">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-propertic-muted">Biggest sale</p>
                    <p className="text-[13px]">
                      {fmtAED(w.biggest_sale.value_aed)} · {w.biggest_sale.area}
                      {w.biggest_sale.project ? ` · ${w.biggest_sale.project}` : ""}
                      {w.biggest_sale.is_offplan ? " · off-plan" : ""}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Areas comparison */}
      <section className="max-w-6xl mx-auto mb-10">
        <h2 className="font-editorial text-2xl mb-4">
          4. Areas: seed vs fresh (top 25 mapped)
        </h2>
        {areas.length === 0 ? (
          <p className="text-sm text-propertic-muted italic">Area aggregation not yet available.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-black/10 bg-white/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-propertic-sand/50 text-left text-[10px] uppercase tracking-[0.1em] text-propertic-muted">
                  <th className="py-2 px-3">Area (market name)</th>
                  <th className="py-2 px-3">DLD AREA_EN</th>
                  <th className="py-2 px-3 tabular-nums">Fresh tx (YTD)</th>
                  <th className="py-2 px-3 tabular-nums">Live seed tx (last Q)</th>
                  <th className="py-2 px-3 tabular-nums">Fresh median AED/sqft</th>
                  <th className="py-2 px-3 tabular-nums">Live seed AED/sqft</th>
                  <th className="py-2 px-3 tabular-nums">Off-plan %</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => {
                  const liveArea = LIVE.areas[a.market_name] || {};
                  return (
                    <tr key={a.area_en_raw} className="border-t border-black/5">
                      <td className="py-2 px-3 font-semibold">{a.market_name}</td>
                      <td className="py-2 px-3 text-[11px] text-propertic-muted font-mono-brand">{a.area_en_raw}</td>
                      <td className="py-2 px-3 tabular-nums">{fmtNum(a.tx_count)}</td>
                      <td className="py-2 px-3 tabular-nums text-propertic-muted">{liveArea.tx_q ? fmtNum(liveArea.tx_q) : "—"}</td>
                      <td className="py-2 px-3 tabular-nums">{a.median_aed_per_sqft ? a.median_aed_per_sqft.toLocaleString() : "—"}</td>
                      <td className="py-2 px-3 tabular-nums text-propertic-muted">{liveArea.aed_sqft ? liveArea.aed_sqft.toLocaleString() : "—"}</td>
                      <td className="py-2 px-3 tabular-nums">{a.offplan_share_pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] text-propertic-muted italic">
          Fresh values are computed from DLD Sales rows (TRANS_VALUE &gt; 0, ACTUAL_AREA ≥ 5 m², converted to sqft). Live seed values come from the previous static H1 2026 snapshot.
        </p>
      </section>

      <footer className="max-w-6xl mx-auto text-[11px] text-propertic-muted font-mono-brand pt-8 border-t border-black/10">
        Data source: Dubai Land Department via the public open-data gateway (Dubai Pulse). Retrieved {new Date().toISOString().slice(0, 10)}. Under the UAE Federal / Dubai Open Data License, commercial republishing is permitted with attribution.
      </footer>

      {loading && <p className="text-center text-[11px] text-propertic-muted mt-6">Loading…</p>}
    </div>
  );
}
